// src/modules/customers/components/CustomerDocumentsSection.jsx
//
// The customer's uploaded documents, one card per type like the pledge case's
// Mandatory Documents. Reference only: uploads are appended one at a time and
// an earlier upload is never replaced. Only an admin can remove one.

import React, { useState } from 'react';
import {
  Card, Row, Col, Upload, Button, Space, Typography, Popconfirm, Tag, Input, Modal, message, Tooltip, Image, Grid,
} from 'antd';
import {
  UploadOutlined, DeleteOutlined, FilePdfOutlined, FileOutlined, FolderOpenOutlined, PlusOutlined, InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { uploadRawImage, addCustomerDocument, removeCustomerDocument } from '../api';
import PhoneCaptureButton from '../../../components/PhoneCapture/PhoneCaptureButton';

const { Text } = Typography;

const DOC_TYPES = [
  { type: 'AADHAR', label: 'Aadhaar' },
  { type: 'PAN', label: 'PAN' },
  { type: 'CURRENT_ADDRESS_PROOF', label: 'Address Proof' },
  { type: 'CHEQUE', label: 'Cheque / Passbook' },
  { type: 'OTHER', label: 'Other Documents' },
];

const extOf = (doc) => {
  const src = doc.file?.name || doc.file?.url || '';
  return src.split('?')[0].split('.').pop().toLowerCase();
};
const isImage = (doc) => ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extOf(doc));

const TILE = 112; // thumbnail size (px)

const tileStyle = {
  width: TILE,
  height: TILE,
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  overflow: 'hidden',
  background: '#fafafa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const fileRefOf = (r = {}) => ({
  url: r.url, fileId: r.fileId, thumbnailUrl: r.thumbnailUrl, filePath: r.filePath, name: r.name,
});

const CustomerDocumentsSection = ({ customerId, documents = [], canUpload, isAdmin, onChanged }) => {
  const screens = Grid.useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();
  const [busy, setBusy] = useState(null);       // documentType being uploaded
  const [removing, setRemoving] = useState(null);
  const [otherPending, setOtherPending] = useState(null); // { file | phoneShot } awaiting a name
  const [otherLabel, setOtherLabel] = useState('');
  const folderPath = `/customer-documents/${customerId}`;

  const save = async (documentType, file, label) => {
    setBusy(documentType);
    try {
      await addCustomerDocument(customerId, { documentType, label, file });
      messageApi.success('Document uploaded');
      onChanged?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to save the document');
    } finally {
      setBusy(null);
    }
  };

  const uploadThenSave = async (documentType, file, label) => {
    setBusy(documentType);
    try {
      const res = await uploadRawImage(file, { fileName: file.name, folderId: folderPath });
      await save(documentType, fileRefOf(res?.data || res), label);
    } catch (err) {
      messageApi.error(`Upload failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`);
      setBusy(null);
    }
  };

  // OTHER needs a name before it is saved, so park the file until one is given.
  const startUpload = (documentType, file) => {
    if (documentType === 'OTHER') {
      setOtherLabel('');
      setOtherPending({ file });
      return;
    }
    uploadThenSave(documentType, file);
  };

  const onPhoneFiles = (documentType, incoming = []) => {
    incoming.forEach((shot) => {
      const ref = fileRefOf({ ...shot, name: shot.name || 'photo.jpg' });
      if (documentType === 'OTHER') {
        setOtherLabel('');
        setOtherPending({ phoneShot: ref });
      } else {
        save(documentType, ref);
      }
    });
  };

  const confirmOther = async () => {
    const label = otherLabel.trim();
    if (!label) { messageApi.warning('Give the document a name'); return; }
    const pending = otherPending;
    setOtherPending(null);
    if (pending.phoneShot) await save('OTHER', pending.phoneShot, label);
    else await uploadThenSave('OTHER', pending.file, label);
  };

  const remove = async (doc) => {
    setRemoving(doc._id);
    try {
      await removeCustomerDocument(customerId, doc._id);
      messageApi.success('Document removed');
      onChanged?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to remove the document');
    } finally {
      setRemoving(null);
    }
  };

  // One thumbnail per upload: images open in a zoomable preview (arrows step
  // through every image of that type), PDFs and other files open in a new tab.
  const renderTile = (doc, isLatest) => {
    const caption = doc.label || (doc.documentType === 'OTHER' ? doc.file?.name : null);
    return (
      <div key={doc._id} style={{ width: TILE, position: 'relative' }}>
        {isImage(doc) ? (
          <div style={tileStyle}>
            <Image
              src={doc.file?.thumbnailUrl || doc.file?.url}
              preview={{ src: doc.file?.url }}
              width={TILE}
              height={TILE}
              style={{ objectFit: 'cover' }}
              alt={caption || doc.documentType}
            />
          </div>
        ) : (
          <a href={doc.file?.url} target="_blank" rel="noopener noreferrer" style={{ ...tileStyle, flexDirection: 'column', gap: 6, textDecoration: 'none' }}>
            {extOf(doc) === 'pdf'
              ? <FilePdfOutlined style={{ fontSize: 36, color: '#cf1322' }} />
              : <FileOutlined style={{ fontSize: 36, color: '#8c8c8c' }} />}
            <Text type="secondary" style={{ fontSize: 11 }}>Open {extOf(doc).toUpperCase()}</Text>
          </a>
        )}

        {isLatest && (
          <Tag color="green" style={{ position: 'absolute', top: 4, left: 4, margin: 0, fontSize: 10, lineHeight: '16px', pointerEvents: 'none' }}>
            Latest
          </Tag>
        )}
        {isAdmin && (
          <Popconfirm title="Remove this document for good?" onConfirm={() => remove(doc)} okText="Remove" okButtonProps={{ danger: true }}>
            <Tooltip title="Remove (admin)">
              <Button
                size="small"
                danger
                shape="circle"
                icon={<DeleteOutlined />}
                loading={removing === doc._id}
                style={{ position: 'absolute', top: 4, right: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}
              />
            </Tooltip>
          </Popconfirm>
        )}

        <div style={{ marginTop: 4, lineHeight: 1.25 }}>
          {caption && <Text strong ellipsis={{ tooltip: caption }} style={{ display: 'block', fontSize: 12 }}>{caption}</Text>}
          <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
            {doc.uploadedAt ? dayjs(doc.uploadedAt).format('DD MMM YYYY, hh:mm A') : ''}
          </Text>
          {doc.uploadedBy?.name && (
            <Text type="secondary" ellipsis={{ tooltip: doc.uploadedBy.name }} style={{ display: 'block', fontSize: 11 }}>
              {doc.uploadedBy.name}
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card
      size="small"
      title={<Space><FolderOpenOutlined /><span>Documents</span><Tag color="blue">{documents.length}</Tag></Space>}
    >
      {contextHolder}
      <Row gutter={[16, 16]}>
        {DOC_TYPES.map(({ type, label }) => {
          // Newest first; every upload is kept.
          const uploads = documents
            .filter((d) => d.documentType === type)
            .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
          const isBusy = busy === type;

          const actions = canUpload && (
            <Space size={6} wrap>
              <Upload
                customRequest={({ file }) => startUpload(type, file)}
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                disabled={isBusy}
              >
                <Button size="small" type={uploads.length ? 'default' : 'primary'} loading={isBusy} icon={uploads.length ? <PlusOutlined /> : <UploadOutlined />}>
                  {uploads.length ? 'Add' : 'Upload'}
                </Button>
              </Upload>
              <PhoneCaptureButton
                targetKey={`customer.document.${type}`}
                targetLabel={`${label} — Customer`}
                folderPath={folderPath}
                recordId={customerId}
                slot={type}
                maxFiles={1}
                onFiles={(files) => onPhoneFiles(type, files)}
                disabled={isBusy}
                buttonText={screens.sm ? 'Phone (QR)' : 'QR'}
                buttonProps={{ size: 'small' }}
              />
            </Space>
          );

          return (
            <Col xs={24} md={type === 'OTHER' ? 24 : 12} xxl={type === 'OTHER' ? 24 : 6} key={type}>
              <Card
                type="inner"
                size="small"
                style={{ height: '100%' }}
                styles={{ header: { flexWrap: 'wrap', gap: 8, paddingBlock: 8 } }}
                title={(
                  <Space size={6}>
                    <Text strong>{label}</Text>
                    {uploads.length > 0 && <Tag style={{ margin: 0 }}>{uploads.length}</Tag>}
                  </Space>
                )}
                extra={actions}
              >
                {uploads.length === 0 ? (
                  <div style={{ border: '1px dashed #d9d9d9', borderRadius: 8, padding: '20px 12px', textAlign: 'center', color: '#8c8c8c' }}>
                    <InboxOutlined style={{ fontSize: 28, display: 'block', marginBottom: 6 }} />
                    <span style={{ fontSize: 12 }}>No {label.toLowerCase()} uploaded yet</span>
                  </div>
                ) : (
                  <Image.PreviewGroup>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {uploads.map((doc, i) => renderTile(doc, i === 0 && uploads.length > 1))}
                    </div>
                  </Image.PreviewGroup>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        title="Name this document"
        open={!!otherPending}
        onOk={confirmOther}
        onCancel={() => setOtherPending(null)}
        okText="Upload"
      >
        <Input
          autoFocus
          placeholder="e.g. Voter ID, Driving Licence, Passport"
          value={otherLabel}
          maxLength={80}
          onChange={(e) => setOtherLabel(e.target.value)}
          onPressEnter={confirmOther}
        />
      </Modal>
    </Card>
  );
};

export default CustomerDocumentsSection;
